import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { API, adminFetch, adminToken } from "../adminApi";
import { LOGO_URL } from "@/data";
import {
  Overview, CreatorsTab, ShopsTab, ProductsTab, ArtistsTab, OrdersTab,
  BookingsTab, EnquiriesTab, AiSalesTab, PromotionsTab, ContentTab, ReportsTab, SettingsTab, AuditTab,
} from "./control";
import {
  LayoutDashboard, Users, Store, Package, Mic, ShoppingBag, CalendarCheck, Mail,
  Bot, Megaphone, FileText, BarChart3, Settings, History,
} from "lucide-react";

/* প্রশাসক-নিয়ন্ত্রণ কক্ষ — হালকা-পরিষ্কার, দ্রুত, গ্রামের রঙে */

const TABS = [
  { key: "overview", label: "এক-নজরে", icon: LayoutDashboard, testid: "tab-overview" },
  { key: "creators", label: "ক্রিয়েটর", icon: Users, testid: "tab-creators" },
  { key: "shops", label: "দোকান", icon: Store, testid: "tab-shops" },
  { key: "products", label: "পণ্য", icon: Package, testid: "tab-products" },
  { key: "artists", label: "শিল্পী", icon: Mic, testid: "tab-artists" },
  { key: "orders", label: "অর্ডার", icon: ShoppingBag, testid: "tab-orders" },
  { key: "bookings", label: "বুকিং", icon: CalendarCheck, testid: "tab-bookings" },
  { key: "enquiries", label: "জিজ্ঞেস", icon: Mail, testid: "tab-enquiries" },
  { key: "aisales", label: "AI-দরদাম", icon: Bot, testid: "tab-aisales" },
  { key: "promotions", label: "প্রচার", icon: Megaphone, testid: "tab-promotions" },
  { key: "content", label: "কনটেন্ট", icon: FileText, testid: "tab-content" },
  { key: "reports", label: "রিপোর্ট", icon: BarChart3, testid: "tab-reports" },
  { key: "settings", label: "সেটিংস", icon: Settings, testid: "tab-settings" },
  { key: "audit", label: "কার্যক্রম-খাতা", icon: History, testid: "tab-audit" },
];

const TITLES = {
  overview: ["এক-নজরে", "আজ গ্রামে কী চলছে"],
  creators: ["ক্রিয়েটর", "কারা গ্রামের ঘর চালান"],
  shops: ["দোকান", "রঙের হাটের ঘরগুলো"],
  products: ["পণ্য", "দোকানের পাট-পত্র"],
  artists: ["শিল্পী", "রং ও মঞ্চের ঠিকানা"],
  orders: ["অর্ডার", "দোকানের জিজ্ঞেস-রসিদ"],
  bookings: ["বুকিং", "মঞ্চ-অনুষ্ঠানের খবর"],
  enquiries: ["জিজ্ঞেস", "গ্রাহকের কথা"],
  aisales: ["AI-দরদাম", "প্রতিনিধিদের লাগাম"],
  promotions: ["প্রচার", "কাকে মুখে মুখে রাখব"],
  content: ["কনটেন্ট", "গ্রামের লেখা-ছবি"],
  reports: ["রিপোর্ট", "সরল হিসাব"],
  settings: ["সেটিংস", "গ্রাম-ব্যবস্থা"],
  audit: ["কার্যক্রম-খাতা", "প্রশাসকের প্রতিটা হাত"],
};

function LoginScreen({ onOk }) {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const res = await fetch(`${API}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password: pw }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "লগইন হয়নি");
      localStorage.setItem("rom_admin_token", d.token);
      onOk();
    } catch (e2) {
      setErr(e2.message);
    }
    setBusy(false);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#f6efe3] px-6" data-testid="admin-login">
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-sm rounded-2xl border border-[#e6dcc8] bg-white p-8 shadow-[0_16px_50px_rgba(61,44,30,0.12)]"
      >
        <img src={LOGO_URL} alt="রং ও মাটি" className="mx-auto h-12 w-auto object-contain" />
        <h1 className="mt-4 text-center font-display text-2xl text-dusk">প্রশাসক-প্রবেশ</h1>
        <p className="mt-1 text-center font-latin text-xs italic tracking-widest text-[#b08234]">ADMIN CONTROL ROOM — RONG-O-MATI</p>

        <label className="mt-6 block">
          <span className="text-xs text-stone-500">ইমেল / মোবাইল</span>
          <input
            type="text"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="admin-login-email"
            className="mt-1 w-full rounded-lg border border-[#e0d4bc] bg-[#fdfaf4] px-3 py-2.5 text-sm text-dusk outline-none transition-colors focus:border-gold"
          />
        </label>
        <label className="mt-4 block">
          <span className="text-xs text-stone-500">পাসওয়ার্ড</span>
          <input
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            data-testid="admin-login-password"
            className="mt-1 w-full rounded-lg border border-[#e0d4bc] bg-[#fdfaf4] px-3 py-2.5 text-sm text-dusk outline-none transition-colors focus:border-gold"
          />
        </label>
        {err && <p data-testid="admin-login-error" className="mt-3 text-sm text-red-600">{err}</p>}
        <button
          type="submit"
          disabled={busy}
          data-testid="admin-login-submit"
          className="mt-6 w-full rounded-full bg-[#C83E2B] py-2.5 text-sm font-medium text-white transition-transform hover:scale-[1.02] disabled:opacity-50"
        >
          {busy ? "ঢুকছি…" : "প্রবেশ করুন"}
        </button>
      </motion.form>
    </div>
  );
}

export default function AdminPanel({ onExit, onSaved, goCustomer }) {
  const [authed, setAuthed] = useState(!!adminToken());
  const [tab, setTab] = useState("overview");
  const [me, setMe] = useState(null);

  useEffect(() => {
    if (!authed) return;
    adminFetch("/admin/me")
      .then((d) => setMe(d.admin || null))
      .catch(() => {
        localStorage.removeItem("rom_admin_token");
        setAuthed(false);
      });
  }, [authed]);

  if (!authed) return <LoginScreen onOk={() => setAuthed(true)} />;

  const isSuper = me?.role === "admin";
  const go = (t) => {
    setTab(t);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const section = () => {
    switch (tab) {
      case "creators": return <CreatorsTab goCustomer={goCustomer} />;
      case "shops": return <ShopsTab goCustomer={goCustomer} />;
      case "products": return <ProductsTab goCustomer={goCustomer} />;
      case "artists": return <ArtistsTab goCustomer={goCustomer} />;
      case "orders": return <OrdersTab />;
      case "bookings": return <BookingsTab />;
      case "enquiries": return <EnquiriesTab />;
      case "aisales": return <AiSalesTab />;
      case "promotions": return <PromotionsTab />;
      case "content": return <ContentTab />;
      case "reports": return <ReportsTab />;
      case "settings": return <SettingsTab me={me} />;
      case "audit": return <AuditTab />;
      default: return <Overview go={go} />;
    }
  };

  const groups = [
    { label: "গ্রাম-হিসাব", keys: ["overview", "reports", "audit"] },
    { label: "মানুষ-ঘর", keys: ["creators", "shops", "products", "artists"] },
    { label: "হাতে-লেখা", keys: ["orders", "bookings", "enquiries", "aisales"] },
    { label: "সাধুনি", keys: ["promotions", "content", "settings"] },
  ];

  return (
    <div className="min-h-screen bg-[#f6efe3]" data-testid="admin-panel">
      <div className="mx-auto flex max-w-[1400px] flex-col px-4 py-5 lg:flex-row lg:gap-8 lg:px-8">
        {/* সাইডবার */}
        <aside className="lg:w-64 lg:shrink-0">
          <div className={`${!isSuper ? "" : ""}flex items-center justify-between rounded-2xl border border-[#e6dcc8] bg-white p-4 lg:block lg:rounded-none lg:border-0 lg:bg-transparent lg:p-0`}>
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="রং ও মাটি" className="h-9 w-auto object-contain" />
              <div>
                <p className="font-display text-lg leading-none text-dusk">RONG-O-MATI</p>
                <p className="mt-1 font-latin text-[10px] italic tracking-widest text-[#b08234]">ADMIN CONTROL ROOM</p>
              </div>
            </div>
            <div className="flex items-center gap-2 lg:mt-4 lg:flex-col lg:items-stretch">
              <span data-testid="admin-role-badge" className="rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-center text-[11px] text-dusk">
                {isSuper ? "মুখ্য-প্রশাসক" : "পরিচালনা-দল"}
              </span>
              <button
                data-testid="admin-logout"
                onClick={async () => {
                  try { await adminFetch("/admin/logout", { method: "POST" }); } catch {}
                  localStorage.removeItem("rom_admin_token");
                  setAuthed(false);
                }}
                className="rounded-full border border-red-300 px-4 py-1.5 text-xs text-red-600 transition-colors hover:bg-red-600 hover:text-white lg:w-full"
              >
                বেরিয়ে যান
              </button>
            </div>
          </div>

          <nav className="mt-4 flex gap-2 overflow-x-auto pb-2 lg:mt-6 lg:flex-col lg:overflow-visible" data-testid="admin-nav">
            {groups.map((g) => (
              <div key={g.label} className="flex shrink-0 gap-1 lg:mb-3 lg:flex-col">
                <p className="hidden px-3 pb-1 text-[10px] uppercase tracking-widest text-stone-400 lg:block">{g.label}</p>
                {TABS.filter((t) => g.keys.includes(t.key)).map((t) => {
                  const Icon = t.icon;
                  return (
                    <button
                      key={t.key}
                      data-testid={t.testid}
                      onClick={() => go(t.key)}
                      className={`flex shrink-0 items-center gap-2.5 rounded-xl px-3.5 py-2 text-left text-sm transition-all duration-300 lg:px-3 ${
                        tab === t.key
                          ? "bg-[#C83E2B] text-white shadow-[0_6px_18px_rgba(200,62,43,0.3)]"
                          : "text-stone-600 hover:bg-white hover:text-dusk"
                      }`}
                    >
                      <Icon size={15} strokeWidth={2} className="shrink-0" />
                      <span className="whitespace-nowrap">{t.label}</span>
                    </button>
                  );
                })}
              </div>
            ))}
          </nav>

          <button
            data-testid="admin-back-village"
            onClick={onExit}
            className="mt-2 hidden w-full rounded-xl border border-[#e0d4bc] px-4 py-2 text-left text-sm text-dusk transition-colors hover:border-gold hover:bg-gold hover:text-white lg:block"
          >
            ← গ্রামে ফিরুন
          </button>
        </aside>

        {/* মূল ঘর */}
        <main className="mt-6 min-w-0 flex-1 lg:mt-0">
          <div className="rounded-2xl border border-[#e6dcc8] bg-white/70 p-5 backdrop-blur-sm sm:p-7" data-testid="admin-main">
            <h2 className="font-display text-2xl text-dusk" data-testid="admin-section-title">{TITLES[tab]?.[0]}</h2>
            <p className="mt-0.5 text-xs text-stone-400">{TITLES[tab]?.[1]}</p>
            <div className="mt-6">{section()}</div>
          </div>
          <button
            data-testid="admin-back-village-mobile"
            onClick={onExit}
            className="mt-4 w-full rounded-xl border border-[#e0d4bc] px-4 py-2.5 text-sm text-dusk transition-colors hover:border-gold hover:bg-gold hover:text-white lg:hidden"
          >
            ← গ্রামে ফিরুন
          </button>
        </main>
      </div>
    </div>
  );
}
